import type { NotificationKind } from '@/services/notifications/types'

const STORAGE_KEY = 'notif_prefs_v1'
const BANNER_DISMISSED_KEY = 'notif_banner_dismissed'

/** Prefs mudaram (mesma aba ou outra) — quem depende re-lê. */
export const PREFS_CHANGE_EVENT = 'notif-prefs-change'
/** Pedido explícito de reabrir o banner de permissão (botão na tela de prefs). */
export const BANNER_SHOW_EVENT = 'notif-banner-show'

export type ChannelPref = 'inbox' | 'sound' | 'browser'

export type KindPrefs = Record<ChannelPref, boolean>

export interface NotificationPrefs {
  soundEnabled: boolean
  browserEnabled: boolean
  /** Por kind: em quais canais aquele kind se manifesta. */
  byKind: Record<NotificationKind, KindPrefs>
  channelActivity: ChannelActivityPrefs
  /** Quanto do conteúdo vai para a bandeja do SO. Ver [[ContentLevel]]. */
  contentLevel: ContentLevel
  /** Notificar o SO mesmo com a janela na frente. Ver [[DEFAULT_SYSTEM_WHEN_FOCUSED]]. */
  systemWhenFocused: boolean
  /** Que número vai para o badge do ícone e para o título da janela. Ver [[BadgeSource]]. */
  badgeSource: BadgeSource
}

/** Casos de `channel_activity` que o usuário pode silenciar. O BADGE nunca entra
 *  aqui: badge é estado, não notificação — some junto com o número, e o número o
 *  usuário pediu. Esta preferência governa só o TOAST. */
export type ChannelActivityPref =
  | 'newMessageOtherChannel'
  | 'newMessageHidden'
  | 'movedIn'
  | 'movedOut'

export type ChannelActivityPrefs = Record<ChannelActivityPref, boolean>

export const CHANNEL_ACTIVITY_PREFS: ChannelActivityPref[] = [
  'newMessageOtherChannel',
  'newMessageHidden',
  'movedIn',
  'movedOut',
]

/** `movedOut` OFF: trabalho SAINDO raramente exige ação e on por default vira ruído
 *  em canal movimentado. Os outros três são trabalho chegando (`movedIn`,
 *  `newMessageOtherChannel`) ou tela que não está mostrando nada (`newMessageHidden`). */
const DEFAULT_CHANNEL_ACTIVITY: ChannelActivityPrefs = {
  newMessageOtherChannel: true,
  newMessageHidden: true,
  movedIn: true,
  movedOut: false,
}

/** `localStorage` é escrito pelo usuário: valor não-booleano cai no default em vez de
 *  vazar truthiness pro `if (prefs.channelActivity.movedIn)`. */
function readChannelActivity(raw: unknown): ChannelActivityPrefs {
  const merged = { ...DEFAULT_CHANNEL_ACTIVITY }
  if (typeof raw !== 'object' || raw === null) return merged
  const stored = raw as Record<string, unknown>
  for (const key of CHANNEL_ACTIVITY_PREFS) {
    const value = stored[key]
    if (typeof value === 'boolean') merged[key] = value
  }
  return merged
}

/**
 * Quanto da notificação chega à BANDEJA DO SO — que na prática é a tela de
 * bloqueio de um aparelho que pode estar na mesa, na mão de outra pessoa ou
 * virado pra cima numa reunião.
 *
 * | nível | título | corpo |
 * |---|---|---|
 * | `full` | nome de quem enviou / título do item | prévia da mensagem |
 * | `sender` | nome de quem enviou / título do item | rótulo genérico |
 * | `generic` | nome do app | rótulo genérico |
 *
 * Default `full`: a notificação nasce ÚTIL, e quem quer privacidade desce o
 * nível de propósito. Ser **por dispositivo** (localStorage) é acerto e não
 * limitação — a privacidade da tela de bloqueio é do aparelho, não da conta: o
 * mesmo usuário quer prévia no desktop de casa e nada no celular do bolso.
 *
 * Governa só a bandeja do SO. O toast in-app e o inbox dentro do app continuam
 * mostrando tudo — ali a superfície já é privada, o usuário está autenticado na
 * frente da tela. Quem aplica é o funil ([[dispatch]]).
 */
export type ContentLevel = 'full' | 'sender' | 'generic'

/** Ordem da UI: do mais informativo ao mais fechado. */
export const CONTENT_LEVELS: ContentLevel[] = ['full', 'sender', 'generic']

const DEFAULT_CONTENT_LEVEL: ContentLevel = 'full'

/** Mesmo molde de `readChannelActivity`: valor desconhecido no storage (versão
 *  antiga, edição manual) cai no default em vez de virar um nível inexistente
 *  que o `switch` do dispatch não sabe tratar. */
function readContentLevel(raw: unknown): ContentLevel {
  return CONTENT_LEVELS.find((level) => level === raw) ?? DEFAULT_CONTENT_LEVEL
}

/**
 * Furar a regra de foco: notificar o SO **mesmo** com a janela na frente e o
 * usuário ativo.
 *
 * Nasce `false` porque o default é a regra de foco+atividade
 * ([[../notifications/foco]]): com a janela na frente o aviso é o toast in-app,
 * que anuncia sem roubar o canto da tela para falar de algo que já está nela.
 * Ligada, todo produtor volta a tentar o SO sempre — que era o comportamento até
 * 2026-09-04, e é o que quem trabalha com a janela pequena num canto da tela
 * quer de volta.
 *
 * Como toda pref deste módulo, é por DISPOSITIVO: "a janela está na minha
 * frente" é um fato do aparelho, não da conta.
 */
const DEFAULT_SYSTEM_WHEN_FOCUSED = false

/**
 * De onde sai o número que chega ao SISTEMA OPERACIONAL — badge do ícone do app
 * instalado e título da janela, os dois juntos.
 *
 * | valor | conta |
 * |---|---|
 * | `messages` | mensagens não lidas somadas de todos os canais do escopo |
 * | `notifications` | notificações NOVAS do inbox (`counts.new`) |
 *
 * Default `messages`: é o número que o rail já mostra e o que estava lá antes de
 * existir escolha. Quem trabalha por fila de avisos troca; quem trabalha por
 * conversa não precisa mexer em nada.
 *
 * **A escolha é sobre QUAL número, nunca sobre TER número** (I4). Badge e título
 * são estado, não notificação: não pedem permissão e não têm opção de desligar.
 * Por isso não existe um terceiro valor `none`.
 *
 * `messages` conta MENSAGENS e não conversas — 20 mensagens de um contato
 * mostram 20, embora seja uma coisa só a resolver. Contar conversas seria a
 * melhor resposta e **exige o back**: o socket entrega `total_unread_messages`
 * já somado por canal, e a lista de chats só existe dentro do `ChannelProvider`,
 * em `/chatbot/[channelId]`.
 */
export type BadgeSource = 'messages' | 'notifications'

/** Ordem da UI. */
export const BADGE_SOURCES: BadgeSource[] = ['messages', 'notifications']

const DEFAULT_BADGE_SOURCE: BadgeSource = 'messages'

/** Mesmo molde de `readContentLevel`: valor fora do union cai no default, senão
 *  o `===` do hook o trataria como "não é notifications" e a tela mentiria sobre
 *  o que está selecionado. */
function readBadgeSource(raw: unknown): BadgeSource {
  return BADGE_SOURCES.find((source) => source === raw) ?? DEFAULT_BADGE_SOURCE
}

export const NOTIF_KINDS: NotificationKind[] = [
  'info',
  'notify',
  'marketing',
  'warning',
  'alert',
  'critical',
]

const CRITICAL_PREFS: KindPrefs = { inbox: true, sound: true, browser: true }

function defaultKindPrefs(kind: NotificationKind): KindPrefs {
  if (kind === 'critical') return { ...CRITICAL_PREFS }
  if (kind === 'marketing') return { inbox: true, sound: false, browser: false }
  return { inbox: true, sound: true, browser: true }
}

/** Tudo no inbox; som/browser globais OFF até o usuário aceitar no banner. */
export function defaultPrefs(): NotificationPrefs {
  const byKind = {} as Record<NotificationKind, KindPrefs>
  for (const kind of NOTIF_KINDS) byKind[kind] = defaultKindPrefs(kind)
  return {
    soundEnabled: false,
    browserEnabled: false,
    byKind,
    channelActivity: { ...DEFAULT_CHANNEL_ACTIVITY },
    contentLevel: DEFAULT_CONTENT_LEVEL,
    systemWhenFocused: DEFAULT_SYSTEM_WHEN_FOCUSED,
    badgeSource: DEFAULT_BADGE_SOURCE,
  }
}

/** Leitura síncrona — serve tanto hooks quanto effects fora do React. */
export function getNotificationPrefs(): NotificationPrefs {
  if (typeof window === 'undefined') return defaultPrefs()
  const base = defaultPrefs()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return base
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>
    return {
      soundEnabled: parsed.soundEnabled ?? base.soundEnabled,
      browserEnabled: parsed.browserEnabled ?? base.browserEnabled,
      byKind: {
        ...base.byKind,
        ...(parsed.byKind ?? {}),
        // Nunca confia no valor salvo: critical é inegociável.
        critical: { ...CRITICAL_PREFS },
      },
      channelActivity: readChannelActivity(parsed.channelActivity),
      contentLevel: readContentLevel(parsed.contentLevel),
      // Mesmo molde de `readChannelActivity`: `??` deixaria passar a string
      // `'sim'` do storage adulterado e ela viraria `true` no `if`.
      systemWhenFocused:
        typeof parsed.systemWhenFocused === 'boolean'
          ? parsed.systemWhenFocused
          : base.systemWhenFocused,
      badgeSource: readBadgeSource(parsed.badgeSource),
    }
  } catch {
    return base
  }
}

function writePrefs(prefs: NotificationPrefs): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT))
}

export function setSoundEnabled(value: boolean): void {
  writePrefs({ ...getNotificationPrefs(), soundEnabled: value })
}

export function setBrowserEnabled(value: boolean): void {
  writePrefs({ ...getNotificationPrefs(), browserEnabled: value })
}

export function setKindChannel(
  kind: NotificationKind,
  channel: ChannelPref,
  value: boolean,
): void {
  if (kind === 'critical') return
  const current = getNotificationPrefs()
  writePrefs({
    ...current,
    byKind: { ...current.byKind, [kind]: { ...current.byKind[kind], [channel]: value } },
  })
}

export function setContentLevel(value: ContentLevel): void {
  writePrefs({ ...getNotificationPrefs(), contentLevel: value })
}

export function setSystemWhenFocused(value: boolean): void {
  writePrefs({ ...getNotificationPrefs(), systemWhenFocused: value })
}

export function setBadgeSource(value: BadgeSource): void {
  writePrefs({ ...getNotificationPrefs(), badgeSource: value })
}

export function setChannelActivityPref(key: ChannelActivityPref, value: boolean): void {
  const current = getNotificationPrefs()
  writePrefs({ ...current, channelActivity: { ...current.channelActivity, [key]: value } })
}

export function isBannerDismissed(): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(BANNER_DISMISSED_KEY) === '1'
}

export function dismissBanner(): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(BANNER_DISMISSED_KEY, '1')
  window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT))
}

export function resetBannerDismissed(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(BANNER_DISMISSED_KEY)
  window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT))
}

/** Reabre o banner agora, independente de dismiss/permission. */
export function showBannerNow(): void {
  if (typeof window === 'undefined') return
  resetBannerDismissed()
  window.dispatchEvent(new CustomEvent(BANNER_SHOW_EVENT))
}
